import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Custom element for displaying recently viewed products in a slider
 * Uses the theme's built-in predictive-search section to fetch products
 */
class RecentlyViewedComponent extends HTMLElement {
  /**
   * The observer for the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#hideIfNoStoredProducts();
    this.#intersectionObserver.observe(this);
  }

  #hideIfNoStoredProducts() {
    const viewedProducts = RecentlyViewed.getProducts();
    const currentProductId = this.dataset.productId;
    const filteredProducts = currentProductId
      ? viewedProducts.filter((id) => String(id) !== String(currentProductId))
      : viewedProducts;

    if (viewedProducts.length === 0 || filteredProducts.length === 0) {
      this.#hideSection();
    }
  }

  /**
   * Load the recently viewed products
   */
  async #loadRecentlyViewed() {
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.#hideSection();
      return;
    }

    const currentProductId = this.dataset.productId;
    const filteredProducts = currentProductId
      ? viewedProducts.filter((id) => String(id) !== String(currentProductId))
      : viewedProducts;

    if (filteredProducts.length === 0) {
      this.#hideSection();
      return;
    }

    const { sectionId, maxProducts } = this.dataset;
    const maxProductsNum = parseInt(maxProducts || '10', 10);
    const limitedProducts = filteredProducts.slice(0, maxProductsNum);
    
    // Use section-recently-viewed-products section (custom section that uses content_for for product cards)
    const targetSectionId = sectionId || 'section-recently-viewed-products';
    
    try {
      const { searchUrl } = this.dataset;
      const url = searchUrl ? new URL(searchUrl, location.origin) : new URL(window.location.pathname, location.origin);
      // Pass product_ids as a parameter for parsing in Liquid
      url.searchParams.set('product_ids', limitedProducts.join(','));
      // Also set search query for products lookup (in case section needs it)
      url.searchParams.set('q', limitedProducts.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      // Filter out out-of-stock products when the section setting is enabled
      if (this.dataset.filterOutOfStock === 'true') {
        url.searchParams.set('filter.v.availability', '1');
      }

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      // Get section HTML using section renderer
      const sectionHTML = await sectionRenderer.getSectionHTML(targetSectionId, false, url);
      
      if (this.#activeFetch.signal.aborted) return;

      // Parse the HTML to extract the slideshow content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      
      // Find the section element
      let sectionElement = doc.querySelector('.shopify-section-recently-viewed-products');
      
      if (!sectionElement) {
        // Try finding by ID pattern
        sectionElement = doc.querySelector(`[id*="recently-viewed-products"]`);
      }
      
      if (!sectionElement) {
        // Try finding slideshow directly
        sectionElement = doc.querySelector('slideshow-component');
      }
      
      if (!sectionElement) {
        // Last resort: use body
        sectionElement = doc.body;
      }
      
      if (!sectionElement || sectionElement === doc.body) {
        // If we can't find the section, try to use the body content directly
        if (doc.body.innerHTML.trim().length > 0) {
          this.innerHTML = doc.body.innerHTML;
          return;
        }
        this.#hideSection();
        return;
      }

      if (sectionElement.innerHTML.trim().length > 0) {
        // Use the entire section content to preserve structure
        this.innerHTML = sectionElement.innerHTML;
      } else {
        this.#hideSection();
      }
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#hideSection();
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  #hideSection() {
    this.classList.add('hidden');
    const section =
      this.closest('[data-jd-recommendations-section]') ||
      this.closest('[data-recently-viewed-section]');
    if (section) {
      section.classList.add('hidden');
    }
  }

}

if (!customElements.get('recently-viewed-component')) {
  customElements.define('recently-viewed-component', RecentlyViewedComponent);
}